import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, Grow } from '@mui/material';
import ClockLoader from '../common/ClockLoader';

const POSCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ type: 'pending', message: 'Processing callback...' });
  const handledKeyRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error') || null;
    const errorDescription = params.get('error_description') || params.get('error_message') || null;
    const code = params.get('code') || null;
    const state = params.get('state') || null;

    // Debug logging so you can see when the callback fires
    // and what we got back from the provider.
    // NOTE: remove or silence these in production if you prefer.
    console.log('[POSCallback] location.search =', location.search);
    console.log('[POSCallback] parsed params:', {
      code,
      state,
      error,
      errorDescription,
    });
    
    // Parse state to get provider info
    let provider = null;
    let source = null;
    if (state) {
      try {
        const stateData = JSON.parse(decodeURIComponent(state));
        provider = stateData.provider;
        source = stateData.source;
      } catch (e) {
        console.error('Failed to parse state:', e);
      }
    }

    // Check if this is opened in a popup (from onboarding)
    const isPopup = window.opener && window.opener !== window;

    if (error || errorDescription) {
      const msg = errorDescription || error || 'Unknown error from provider';
      setStatus({ type: 'error', message: msg });
      
      // If opened from popup, notify parent window
      if (isPopup && provider) {
        window.opener.postMessage({
          type: 'POS_OAUTH_ERROR',
          provider: provider,
          error: msg
        }, window.location.origin);
        // Close popup after a short delay
        setTimeout(() => window.close(), 2000);
        return;
      }
      
      // Auto-redirect to dashboard after 3s
      const t = setTimeout(() => navigate('/dashboard'), 3000);
      return () => clearTimeout(t);
    }

    if (code) {
      // In React dev (StrictMode), effects can run twice. Guard so we only
      // process/postMessage once per provider+code.
      const key = `${provider || 'unknown'}:${code}`;
      if (handledKeyRef.current === key) {
        return;
      }
      handledKeyRef.current = key;

      // Received auth code — show success and redirect. Backend should exchange code for token.
      console.log('[POSCallback] Received POS OAuth code:', { provider, source, code });
      setStatus({ type: 'success', message: 'Authorization successful. Redirecting...' });

      // If opened from popup (normal onboarding flow), notify parent window
      if (isPopup && provider) {
        window.opener.postMessage(
          {
            type: 'POS_OAUTH_SUCCESS',
            provider: provider,
            code: code,
          },
          window.location.origin
        );
        // Close popup after a short delay
        setTimeout(() => window.close(), 1500);
        return;
      }

      // Non-popup: user opened callback directly in main window.
      // In that case, send them back to onboarding instead of dashboard.
      const t = setTimeout(() => navigate('/onboarding'), 1500);
      return () => clearTimeout(t);
    }

    // No recognizable params — go back to dashboard
    if (isPopup) {
      window.close();
      return;
    }
    
    const t = setTimeout(() => navigate('/dashboard'), 1500);
    return () => clearTimeout(t);
  }, [location.search, navigate]);

  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Grow in>
        <Box sx={{ textAlign: 'center' }}>
          {status.type === 'pending' && <ClockLoader size={72} />}
          {status.type === 'error' && (
            <Box>
              <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>{status.message}</Alert>
              <Typography variant="body2" color="text.secondary">Redirecting to dashboard...</Typography>
            </Box>
          )}
          {status.type === 'success' && (
            <Box>
              <Alert severity="success" sx={{ mb: 2, fontWeight: 700 }}>Connected — finishing setup.</Alert>
              <Typography variant="body2" color="text.secondary">Redirecting to dashboard...</Typography>
            </Box>
          )}
        </Box>
      </Grow>
    </Box>
  );
};

export default POSCallback;
